import { Injectable, signal, inject } from '@angular/core'
import { ToggleMfaBody, User } from '../types/users'
import { environment } from '../../environments/environment'
import { Observable, catchError, tap } from 'rxjs'
import { HttpClient } from '@angular/common/http'
import { ErrorService } from './error.service'
import {
  LoginBody,
  RefreshAccessTokenResponse,
  SetPasswordBody,
  Tokens,
  ResetPasswordBody,
  LoginResponse,
  Session,
  SessionResponse,
} from '../types/auth'
import { RootInjectorGuard } from '../guards/root-injector.guard'
import { sessionLSKey } from '../constants/auth'

@Injectable({
  providedIn: 'root',
})
export class AuthService extends RootInjectorGuard {
  private http = inject(HttpClient)
  private errorService = inject(ErrorService)

  private URL = environment.baseUrl + 'auth'
  public isLoggedIn = signal(false)
  public accessToken = signal('')
  public refreshToken = signal('')
  public currentUserRole = signal('')

  constructor() {
    super(AuthService)
  }

  /**
   * Sets signal that indicates if a user is currently logged in
   */
  setIsLoggedIn() {
    const sessionData = localStorage.getItem(sessionLSKey)
    this.isLoggedIn.set(sessionData !== null)
  }

  /**
   * Sets the values of the access and refresh token signals
   * @param {Tokens} tokens The tokens
   */
  setTokens(tokens: Tokens | undefined) {
    if (!tokens) {
      const sessionDataLS = localStorage.getItem(sessionLSKey)
      if (sessionDataLS && sessionDataLS.length) {
        const sessionData: Session = JSON.parse(sessionDataLS) as Session
        tokens = {
          refreshToken: sessionData.refreshToken,
          accessToken: sessionData.accessToken,
        }
      } else {
        return
      }
    }

    this.accessToken.set(tokens.accessToken)
    this.refreshToken.set(tokens.refreshToken)
  }

  /**
   * Refreshes the access token
   * @returns {Observable<RefreshAccessTokenResponse>} The request response
   */
  refreshAccessToken(): Observable<RefreshAccessTokenResponse> {
    return this.http
      .post<RefreshAccessTokenResponse>(this.URL + '/refresh-access-token', {
        refreshToken: this.refreshToken(),
      })
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Validates a password token
   * @param {string} passwordToken Password token to be validated
   * @returns {Observable<User>} The user found for the token validation
   */
  validatePasswordToken(passwordToken: string): Observable<User> {
    return this.http
      .get<User>(this.URL + `/password/validate/${passwordToken}`)
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Sets a newly registered user's password
   * @param {SetPasswordBody} passwordBody Request body
   * @returns {Observable<void>}
   */
  setPassword(passwordBody: SetPasswordBody): Observable<void> {
    return this.http
      .put<void>(this.URL + '/password', passwordBody)
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Sends request to login a user
   * @param {SetPasswordBody} loginBody Request body
   * @returns {LoginResponse} Session data for the user if mfa is disabled. MFA data otherwise
   */
  login(loginBody: LoginBody): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(this.URL + '/login', loginBody)
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Sends the request to log out a user. If successful, removes the session data from local storage.
   * @returns {Observable<void>}
   */
  logout(): Observable<void> {
    return this.http.post<void>(this.URL + '/logout', {}).pipe(
      tap(() => {
        this.removeSessionFromLS()
      }),
      catchError(this.errorService.handleError),
    )
  }

  /**
   * Adds current user session data to local storage
   * @param {Session} session Session data
   */
  storeSessionInLS(session: Session) {
    localStorage.setItem(sessionLSKey, JSON.stringify(session))
    const tokens: Tokens = {
      refreshToken: session.refreshToken,
      accessToken: session.accessToken,
    }
    this.setTokens(tokens)
    this.isLoggedIn.set(true)
  }

  /**
   * Removes current user session data from local storage
   */
  removeSessionFromLS() {
    this.accessToken.set('')
    this.refreshToken.set('')
    localStorage.removeItem(sessionLSKey)
    this.isLoggedIn.set(false)
  }

  /**
   * Retrieves current user session data from local storage
   * @returns {Session | undefined } The session (if any)
   */
  getSessionFromLS() {
    try {
      const sessionLS = localStorage.getItem(sessionLSKey)
      if (sessionLS) {
        const session = JSON.parse(sessionLS) as Session
        return session
      }
      return undefined
    } catch {
      return undefined
    }
  }

  /**
   * Sends request to reset a user's password
   * @param {ResetPasswordBody} resetPasswordBody Request body
   * @returns {Observable<void>}
   */
  resetPassword(resetPasswordBody: ResetPasswordBody): Observable<void> {
    return this.http
      .post<void>(this.URL + '/reset-password', resetPasswordBody)
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Checks if the current logged in user has a role
   * @param {string} role The role to validate
   * @returns {boolean} Flag indicating if the logged in user has the role
   */
  validateRole(role: string): boolean {
    const sessionData = this.getSessionFromLS()
    return sessionData?.user.role === role
  }

  /**
   * Sends a request to verify the Multi-Factor Authentication (MFA) code submitted by the user.
   * If the verification is successful, it returns the user's session response.
   * @param {string} userId - The unique identifier of the user attempting verification.
   * @param {string} code - The MFA code provided by the user for verification.
   * @returns {Observable<SessionResponse>} An observable containing the session response on successful verification.
   */
  verifyMfaCode(userId: string, code: string): Observable<SessionResponse> {
    return this.http
      .post<SessionResponse>(`${this.URL}/mfa/verify`, { userId, code })
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Sends a request to resend MFA code to the user's email address.
   * @param {string} userId - The unique identifier of the user attempting verification.
   * @returns {Observable<void>} An observable containing the session response on successful verification.
   */
  resendMfaCode(userId: string): Observable<void> {
    return this.http
      .post<void>(`${this.URL}/mfa/resend`, { userId })
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Sends a request to toggle MFA requirement status for a user
   * @param {ToggleMfaBody} toggleRequest - The request data
   * @returns {Observable<void>} An observable containing the session response on successful verification.
   */
  toggleMfa(toggleRequest: ToggleMfaBody): Observable<void> {
    return this.http
      .put<void>(`${this.URL}/mfa/toggle`, toggleRequest)
      .pipe(catchError(this.errorService.handleError))
  }
}
